import { useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertCircle, Send } from "lucide-react";
import { toast } from "sonner";
import KineticMarquee from "@/components/KineticMarquee";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  social_handle: "",
  location: "",
  topics: "",
  why_you: "",
  extra: "",
};

export default function Apply() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, form);
      setSuccess(true);
      setForm(initial);
      toast.success("Application sent. Sav will be in touch.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Try again.";
      setError(typeof msg === "string" ? msg : "Submission failed.");
      toast.error("Couldn't send application.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="apply-page"
      className="px-6 lg:px-12 py-16 lg:py-24 pb-40 text-[var(--tsws-green)]"
    >
      <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
        <div className="lg:col-span-8">
          <span className="font-label text-xs text-[var(--tsws-cigar)]">⏤ Guest Application</span>
          <h1 className="font-display text-7xl md:text-8xl lg:text-[10rem] mt-3 leading-[0.82]">
            be on<br />
            the sesh<span className="text-[var(--tsws-cigar)]">.</span>
          </h1>
        </div>
        <div className="lg:col-span-4">
          <p className="font-tag text-xl">Pull up a stool.</p>
          <p className="mt-3 leading-relaxed opacity-85">
            Tell us who you are and what you'd want to chat about. We read every submission and
            respond to the ones that catch our ear.
          </p>
        </div>
      </div>

      <KineticMarquee phrases={["hit us up", "tell your story", "we read every one"]} />

      <div className="mt-16 grid lg:grid-cols-12 gap-12">
        {/* Side info */}
        <aside className="lg:col-span-4 space-y-8">
          <div className="border-2 border-[var(--tsws-green)] bg-[var(--tsws-paper)] p-6">
            <div className="font-display text-xl text-[var(--tsws-cigar)] mb-3">
              what we look for
            </div>
            <ul className="space-y-3">
              <li className="flex gap-2">
                <span className="text-[var(--tsws-cigar)]">→</span> A story worth an hour.
              </li>
              <li className="flex gap-2">
                <span className="text-[var(--tsws-cigar)]">→</span> Willingness to keep it real.
              </li>
              <li className="flex gap-2">
                <span className="text-[var(--tsws-cigar)]">→</span> Any niche — music, fight, hustle, weird.
              </li>
              <li className="flex gap-2">
                <span className="text-[var(--tsws-cigar)]">→</span> Aussies & internationals welcome.
              </li>
            </ul>
          </div>
          <div className="border-2 border-[var(--tsws-green)] bg-[var(--tsws-paper)] p-6">
            <div className="font-display text-xl text-[var(--tsws-cigar)] mb-3">how it works</div>
            <ol className="space-y-3 list-decimal list-inside">
              <li>You send the form.</li>
              <li>Sav reads it (yes, actually).</li>
              <li>You hear back within 2 weeks if it's a fit.</li>
              <li>We book a time and pour the first round.</li>
            </ol>
          </div>
        </aside>

        {/* Form */}
        <form
          onSubmit={submit}
          data-testid="apply-form"
          className="lg:col-span-8 border-2 border-[var(--tsws-green)] p-6 lg:p-10 bg-[var(--tsws-paper)]"
        >
          {success && (
            <div
              data-testid="apply-success"
              className="mb-8 border-2 border-[var(--tsws-green)] bg-[var(--tsws-cream)] p-5 flex items-start gap-3"
            >
              <CheckCircle2 className="text-[var(--tsws-green)] mt-1 shrink-0" />
              <div>
                <div className="font-display text-3xl text-[var(--tsws-green)]">
                  you're on the list.
                </div>
                <p className="mt-1">
                  Application received. We'll be in touch if Sav wants you in the studio.
                </p>
              </div>
            </div>
          )}
          {error && (
            <div
              data-testid="apply-error"
              className="mb-8 border-2 border-[var(--tsws-cigar)] bg-[var(--tsws-orange-light)] p-5 flex items-start gap-3"
            >
              <AlertCircle className="text-[var(--tsws-cigar)] mt-1 shrink-0" />
              <p>{error}</p>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            <Field label="Your name *" testId="field-name">
              <input
                data-testid="input-name"
                required
                maxLength={120}
                value={form.name}
                onChange={update("name")}
                className="brutal-input"
                placeholder="Mate Mateson"
              />
            </Field>
            <Field label="Email *" testId="field-email">
              <input
                data-testid="input-email"
                required
                type="email"
                value={form.email}
                onChange={update("email")}
                className="brutal-input"
                placeholder="you@example.com"
              />
            </Field>
            <Field label="Social handle" testId="field-social">
              <input
                data-testid="input-social"
                value={form.social_handle}
                onChange={update("social_handle")}
                className="brutal-input"
                placeholder="@yourhandle or link"
              />
            </Field>
            <Field label="Where you based?" testId="field-location">
              <input
                data-testid="input-location"
                value={form.location}
                onChange={update("location")}
                className="brutal-input"
                placeholder="Perth, AU"
              />
            </Field>
          </div>

          <div className="mt-6">
            <Field label="What do you want to chat about? *" testId="field-topics">
              <textarea
                data-testid="input-topics"
                required
                maxLength={2000}
                rows={4}
                value={form.topics}
                onChange={update("topics")}
                className="brutal-input resize-y"
                placeholder="Two or three topics that'd make a sick episode..."
              />
            </Field>
          </div>

          <div className="mt-6">
            <Field label="Why should you be on the show? *" testId="field-why">
              <textarea
                data-testid="input-why"
                required
                maxLength={2000}
                rows={5}
                value={form.why_you}
                onChange={update("why_you")}
                className="brutal-input resize-y"
                placeholder="Your background, your story, your edge. Don't be modest, but don't be a wanker either."
              />
            </Field>
          </div>

          <div className="mt-6">
            <Field label="Anything else?" testId="field-extra">
              <textarea
                data-testid="input-extra"
                rows={3}
                maxLength={2000}
                value={form.extra}
                onChange={update("extra")}
                className="brutal-input resize-y"
                placeholder="Availability, links, deal-breakers, the lot."
              />
            </Field>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <p className="font-tag text-sm opacity-70 max-w-md">
              We keep your details private. By submitting you agree to be contacted about being on
              the show.
            </p>
            <button
              type="submit"
              data-testid="guest-submit-btn"
              disabled={submitting}
              className="font-display text-lg px-8 py-4 border-2 border-[var(--tsws-green)] bg-[var(--tsws-green)] text-[var(--tsws-orange)] hover-brutal hover:bg-[var(--tsws-cigar)] hover:border-[var(--tsws-cigar)] inline-flex items-center gap-3 disabled:opacity-60"
            >
              {submitting ? "sending..." : "send application"} <Send size={16} />
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .brutal-input {
          width: 100%;
          background: var(--tsws-orange);
          color: var(--tsws-green);
          border: 2px solid var(--tsws-green);
          padding: 14px 16px;
          font-family: 'Fraunces', Georgia, serif;
          font-size: 16px;
          outline: none;
          transition: background 200ms ease, color 200ms ease, box-shadow 200ms ease;
        }
        .brutal-input::placeholder { color: var(--tsws-green); opacity: 0.45; }
        .brutal-input:focus {
          background: var(--tsws-green);
          color: var(--tsws-orange);
          box-shadow: 4px 4px 0 0 var(--tsws-cigar);
        }
        .brutal-input:focus::placeholder { color: var(--tsws-orange); opacity: 0.5; }
      `}</style>
    </div>
  );
}

const Field = ({ label, testId, children }) => (
  <label data-testid={testId} className="block">
    <span className="block font-label text-xs mb-2 opacity-80">{label}</span>
    {children}
  </label>
);
